import React, { useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { MdOutlineMap, MdFormatListBulleted } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import { useToasts } from '../../hooks';
import { withContextProvider } from '../../hoc';
import { DialogsProvider, ToastProvider } from '../../providers';
import Site from '../../componentsV2/Sites/Site';
import Pagination from '../../componentsV2/pagination/Pagination';
import tailwindTheme from '../../../../../config/tailwind.config';
import SitesTable from '../../componentsV2/Sites/SitesTable';
import SitesMap from '../../componentsV2/Sites/SitesMap';
import Button from '../../componentsV2/Button/Button';
import { AppsRounded, Plus } from '../../componentsV2/Icons/Icons';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import AdvancedSearchWrapper from '../../componentsV2/AdvancedSearch/AdvancedSearchWrapper';
import { handleResponse } from '../../utils/api/errorHandler';
import { SITE_DEFAULT_SEARCH_PARAMS } from '../../constants/sites';
import { getSearchObjectFromUrlParams } from '../../utils/urlToSearchParams';
import cleanUpObject from '../../utils/cleanUpObject';
import { getSites } from '../../utils/api/site';
import EmptyState from '../../componentsV2/EmptyState/EmptyState';
import NoSearchResults from '../../componentsV2/NoSearchResults/NoSearchResults';
import { redirectToNewSite } from '../../utils/navigation';
import TableSkeletonLoader from '../../componentsV2/LoadingSkeletons/TableSkeletonLoader';
import MapSkeletonLoader from '../../componentsV2/LoadingSkeletons/MapSkeletonLoader';
import SitesCardSkeletonLoader from '../../componentsV2/LoadingSkeletons/SitesCardSkeletonLoader';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import CustomSelect from '../../componentsV2/FormInputs/CustomSelect/CustomSelect';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';
import TagModal from '../../componentsV2/Tags/TagsTabbedModal';
import { fetchTagsForSites } from '../../utils/tagUtils';
import { excludedKeys } from '../../utils/advancedSearch';
import ViewModeTooltip from '../../componentsV2/ViewModeTooltip/ViewModeTooltip';

const SITES_TABLE_LIMIT_OPTIONS = [20, 100];

const SitesPage = ({ organizationId }) => {
  const { showToast } = useToasts();
  const [sites, setSites] = useState([]);
  const [viewType, setViewType] = useUrlSearchParams('mode', 'table'); // Custom hook useUrlSearchParams is for switching between different views (modes)
  const [sort, setSort] = useUrlSearchParams('sort', 'name'); // Track sorting parameters in URL
  const [ordering, setOrdering] = useUrlSearchParams('ordering', 'asc'); // Default to 'asc'
  const [totalSites, setTotalSites] = useState(null);
  const [unfilteredTotalSites, setUnfilteredTotalSites] = useState(0);
  const [isLoading, setIsLoading] = useState(true); // State for loading
  const [isSwitchingMode, setIsSwitchingMode] = useState(false);
  const [hasBeenSearched, setHasBeenSearched] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [existingTags, setExistingTags] = useState([]);

  const currentPageURL = window.location.search;
  const urlSearchParams = new URLSearchParams(currentPageURL);
  const currentPage = parseInt(urlSearchParams.get('page'), 10) || 1;

  const breadcrumbLinks = [{ href: '/sites', name: 'Sites' }];

  // Reusable function to determine the default limit based on view type
  const getLimitByViewType = (currentViewType, searchParams) => {
    if (currentViewType === 'gallery') {
      return 24; // 24 items per page for gallery view
    }

    if (currentViewType === 'table') {
      const urlLimit = parseInt(searchParams.get('limit'), 10); // Get the 'limit' from URL params

      // Defaults to the first table page size if the URL limit is missing/invalid or not one of the allowed values.
      return SITES_TABLE_LIMIT_OPTIONS.includes(urlLimit) ? urlLimit : SITES_TABLE_LIMIT_OPTIONS[0];
    }

    return SITES_TABLE_LIMIT_OPTIONS[0];
  };

  // Use the function to get the current limit
  const currentLimit = getLimitByViewType(viewType, urlSearchParams);

  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [sitesAttributes, setSitesAttributes] = useState({});
  const [shouldSearch, setShouldSearch] = useState(true);
  const [limit, setLimit] = useState(currentLimit); // Initialize limit based on the view type

  // Get the latest searchParams by merging SITE_DEFAULT_SEARCH_PARAMS,
  // urlSearchParams that reads from url, organization id, and limit.
  const parseQueryParams = useCallback(() => {
    const updatedUrlParams = new URLSearchParams(window.location.search);
    const searchParamsObject = getSearchObjectFromUrlParams(updatedUrlParams);

    const mode = updatedUrlParams.get('mode') || 'table';
    const computedLimit = getLimitByViewType(mode, updatedUrlParams);
    const isGallery = mode === 'gallery';

    return {
      ...SITE_DEFAULT_SEARCH_PARAMS,
      ...searchParamsObject,
      organization_id: organizationId,
      limit: computedLimit,
      offset: parseInt(updatedUrlParams.get('offset'), 10) || 0,
      sort: updatedUrlParams.get('sort') || (isGallery ? 'updated_at' : 'name'),
      ordering: updatedUrlParams.get('ordering') || (isGallery ? 'desc' : 'asc'),
      page: parseInt(updatedUrlParams.get('page'), 10) || 1,
      mode,
    };
  }, [organizationId]);

  const [searchParams, setSearchParams] = useState(parseQueryParams);
  const [activeFilters, setActiveFilters] = useState(searchParams);

  useEffect(() => {
    if (shouldSearch) {
      getSites(searchParams)
        .then((data) => {
          if (!data.sites) {
            throw new Error(data.error);
          }
          setSites(data.sites);
          setTotalSites(data.result_count);
          setUnfilteredTotalSites(data.total_count);
        })
        .catch((error) => {
          showToast({ message: error.message, type: 'error' });
        })
        .finally(() => {
          setShouldSearch(false); // Reset shouldSearch after fetching
          // Wait for data loading
          setTimeout(() => {
            setIsLoading(false);
          }, 200);
        });
    }
  }, [shouldSearch, organizationId, currentPage, searchParams, limit, showToast]);

  const updateSearchParamsAndUrl = (params) => {
    cleanUpObject(params);
    const newURL = new URLSearchParams(params).toString();
    window.history.pushState({}, '', `?${newURL}`);
    const updatedParams = parseQueryParams();
    setSearchParams(updatedParams);
    setShouldSearch(true);
    setIsLoading(true);
  };

  useEffect(() => {
    const meaningfulParams = Object.entries(searchParams).filter(([key, value]) => !excludedKeys.includes(key) && value && value !== '');

    setHasBeenSearched(meaningfulParams.length > 0);
  }, [searchParams]);

  const handlePageChange = (page) => {
    if (page < 1 || page > Math.ceil(totalSites / limit)) return; // Ensure valid page

    const updatedSearchParams = {
      ...searchParams,
      offset: (page - 1) * limit,
      page,
      mode: viewType,
    };

    updateSearchParamsAndUrl(updatedSearchParams);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit); // Update the limit state
    const updatedSearchParams = {
      ...searchParams,
      limit: newLimit,
      mode: viewType,
      offset: 0,
      page: 1,
    };
    updateSearchParamsAndUrl(updatedSearchParams);
  };

  // handle search options population
  useEffect(() => {
    fetch(`/api/v3/sites_attributes?organization_id=${organizationId}`)
      .then(handleResponse)
      .then((data) => {
        setSitesAttributes(data);
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  }, [organizationId, showToast]);

  const search = () => {
    const updatedSearchParams = {
      ...activeFilters,
      mode: viewType,
      offset: 0,
    };

    if (viewType !== 'map') {
      updatedSearchParams.page = 1;
      updatedSearchParams.limit = limit;
    }

    updateSearchParamsAndUrl(updatedSearchParams);
  };

  // Handle sorting by updating searchParams and resetting to the first page
  const handleSort = (field) => {
    const defaultDescFields = ['created_at', 'updated_at', 'last_visit'];
    const isDescFirst = defaultDescFields.includes(field);
    const updatedSort = field === 'priority' ? 'priority_int' : field;
    let newOrdering;
    if (sort === field || sort === 'priority_int') {
      newOrdering = ordering === 'asc' ? 'desc' : 'asc';
    } else {
      newOrdering = isDescFirst ? 'desc' : 'asc';
    }
    // Update the searchParams to include new sorting values
    const updatedSearchParams = {
      ...searchParams,
      sort: updatedSort,
      limit,
      mode: viewType,
      ordering: newOrdering,
      offset: 0,
      page: 1, // Reset to the first page when sorting
    };

    // Update both URL and state
    updateSearchParamsAndUrl(updatedSearchParams);
    setSort(updatedSort);
    setOrdering(newOrdering);
  };

  // Change the view mode and reset to page 1
  const handleModeChange = (mode) => {
    setIsSwitchingMode(true);

    let updatedSearchParams;

    if (mode === 'map') {
      // For map view, only mode is relevant
      updatedSearchParams = {
        ...searchParams,
        mode,
      };
      delete updatedSearchParams.limit;
      delete updatedSearchParams.page;
      setLimit(null);
    } else {
      const updatedLimit = getLimitByViewType(mode, urlSearchParams); // Get the correct limit for the new view type
      updatedSearchParams = {
        ...searchParams,
        limit: updatedLimit, // Set limit based on mode
        mode,
        offset: 0,
        page: 1, // Reset to the first page when switching views
      };
      if (mode === 'gallery') {
        updatedSearchParams.sort = 'updated_at';
        updatedSearchParams.ordering = 'desc';
        setSort('updated_at');
        setOrdering('desc');
      } else if (viewType === 'gallery') {
        updatedSearchParams.sort = 'name';
        updatedSearchParams.ordering = 'asc';
        setSort('name');
        setOrdering('asc');
      }
      setLimit(updatedLimit);
    }

    updateSearchParamsAndUrl(updatedSearchParams);
    setViewType(mode);

    // Wait for data loading
    setTimeout(() => {
      setIsSwitchingMode(false);
    }, 500);
  };

  useEffect(() => {
    const handlePopState = () => {
      const updatedParams = parseQueryParams();
      setSearchParams(updatedParams);
      setShouldSearch(true);
      setIsLoading(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [parseQueryParams]);

  // Render the skeleton loader based on the view type
  const renderSkeletonLoader = () => {
    if (viewType === 'table') {
      return (
        <div className="pt-36.5">
          <TableSkeletonLoader numRows={limit} numColumns={8} skeletonHeight={16} skeletonWidth="70%" />;
        </div>
      );
    }
    if (viewType === 'map') {
      return (
        <div className="flex flex-col items-center">
          <div className="pt-20.5 w-full h-full">
            <MapSkeletonLoader />
          </div>
        </div>
      );
    }
    // Generating UUIDs for the loaders
    const loaderKeys = Array.from({ length: limit }, () => uuidv4());
    return (
      <div className="flex flex-col items-center">
        <div className="pt-33.25 w-full">
          <div className="grid justify-center grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-6 gap-x-6 gap-y-8">
            {loaderKeys.map((key) => (
              <SitesCardSkeletonLoader key={key} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render the view based on the view type
  const renderView = () => {
    if (viewType === 'table') {
      return <SitesTable sites={sites} onSort={handleSort} />;
    }
    if (viewType === 'map') {
      return <SitesMap sitesArray={sites} />;
    }

    return (
      <div data-testid="sites-list" className="grid justify-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-6 gap-x-6 gap-y-8">
        {sites.map((site) => (
          <Site site={site} key={site.id} />
        ))}
      </div>
    );
  };

  const viewModeButtons = [
    { mode: 'table', label: 'Table View', Icon: MdFormatListBulleted },
    { mode: 'map', label: 'Map View', Icon: MdOutlineMap },
    { mode: 'gallery', label: 'Card View', Icon: AppsRounded },
  ];

  const renderSearchSection = () => {
    if (isLoading) return null;
    return (
      <AdvancedSearchWrapper
        setIsSearchExpanded={setIsSearchExpanded}
        sitesAttributes={sitesAttributes}
        params={activeFilters}
        setParams={setActiveFilters}
        search={search}
        searchParams={searchParams}
        setSearchParams={setActiveFilters}
        updateSearchParamsAndUrl={updateSearchParamsAndUrl}
        isSearchExpanded={isSearchExpanded}
      />
    );
  };

  const options = [
    { label: 'A-Z', value: 'name' },
    { label: 'Recently updated', value: 'updated_at' },
    { label: 'Priority', value: 'priority_int' },
  ];

  const handleSitesSort = (selected) => {
    const newParams = {
      sort: selected.value,
      ordering: selected.value === 'name' ? 'asc' : 'desc',
    };

    // Update the searchParams to include new sorting values
    const updatedSearchParams = {
      ...searchParams,
      ...newParams,
      offset: 0,
      page: 1,
      mode: viewType,
    };

    // Update both URL and state
    updateSearchParamsAndUrl(updatedSearchParams);

    setSort(selected.value);
  };

  const handleOpenTagModal = async () => {
    try {
      const siteIds = sites.map((s) => s.id);
      const tags = await fetchTagsForSites(siteIds, organizationId, showToast);

      setExistingTags(tags);
      setIsTagModalOpen(true);
    } catch (error) {
      showToast({ message: error.message, type: 'error' });
    }
  };

  const getSortPlaceholder = () => {
    const option = options.find((opt) => opt.value === searchParams.sort);
    return option ? option.label : 'Recently updated';
  };

  const renderContent = () => {
    if (isSwitchingMode || isLoading) {
      return renderSkeletonLoader();
    }

    if (sites.length === 0) {
      if (unfilteredTotalSites === 0) {
        return <EmptyState pageType="sites" onAdd={redirectToNewSite} />;
      }
      return <NoSearchResults pageType="sites" />;
    }

    const buildOptions = (array) => array?.map((item) => ({
      label: item,
      value: item,
    }));
    const fieldOptions = buildOptions(SITES_TABLE_LIMIT_OPTIONS);

    return (
      <>
        <div className={viewType === 'table' ? '' : 'flex flex-col'}>
          <div className="flex w-full justify-between pb-4 gap-4">
            <PagesHeader name="site" label="Sites" size="h-10" data-testid="sites-page-header" />
            {/* Displays paginated result count for table and gallery views, adapting to search */}
            {viewType !== 'map' && totalSites > 0 && (
              <ResultCount
                offset={searchParams.offset}
                pageSize={sites.length}
                total={totalSites}
                unfilteredTotal={unfilteredTotalSites}
                viewType={viewType}
                pageType="sites"
              />
            )}
            {/* Displays paginated result count for map view */}
            {viewType === 'map' && sites.length > 0 && (
              <div className="flex justify-center pb-4">
                <ResultCount
                  offset={0}
                  pageSize={sites.length}
                  total={sites.length}
                  unfilteredTotal={sites.length}
                  viewType={viewType}
                  pageType={hasBeenSearched ? 'results' : 'sites'}
                />
              </div>
            )}
            <div className="flex gap-4 items-center">
              {/* Stable selector for feature specs opening the create form. */}
              <Button label="New Site" Icon={Plus} className="mr-8" onClick={redirectToNewSite} dataTestId="new-site-button" />
              {hasBeenSearched && (
                <Button
                  label={`Tag ${sites.length} site${sites.length === 1 ? '...' : 's...'}`}
                  color="secondary"
                  onClick={handleOpenTagModal}
                  isDisabled={!sites.length}
                />
              )}
              {viewType === 'gallery' && (
                <CustomSelect
                  value={options.find((opt) => opt.value === searchParams.sort)}
                  options={options}
                  setValue={(selected) => {
                    handleSitesSort(selected);
                  }}
                  placeholder={getSortPlaceholder()}
                  inputPadding="0"
                  dataTestId="gallery-sort-select"
                />
              )}
              {/* Select Dropdown */}
              {viewType === 'table' && (
                <CustomSelect
                  value={fieldOptions.find((opt) => opt.value === limit)}
                  options={fieldOptions} // Options for limit
                  setValue={(selected) => handleLimitChange(selected.value)}
                  isValid
                  borderColor="border-vamos-blue-gray"
                  className="w-16 h-8 min-w-16 min-h-8 pl-2"
                  disableEmptyOption
                  inputPadding="0"
                  dataTestId="pagination-dropdown"
                />
              )}
              {viewModeButtons.map(({ mode, label, Icon }) => (
                <ViewModeTooltip
                  key={mode}
                  id={`sites-${mode}-view-tooltip`}
                  label={label}
                >
                  <button
                    data-testid={`${mode}-mode`}
                    type="button"
                    onClick={() => handleModeChange(mode)}
                    aria-label={label}
                    className={`flex justify-center items-center w-8 h-8 min-w-8 min-h-8 rounded-md border-none bg-${viewType === mode ? 'vamos-green1' : 'vamos-white'} focus:outline-none`}
                  >
                    <Icon
                      size={24}
                      color={`${viewType === mode
                        ? tailwindTheme.theme.extend.colors['vamos-white']
                        : tailwindTheme.theme.extend.colors['vamos-black']}`}
                      className={`flex p-1 w-8 h-8 min-w-8 min-h-8 items-center justify-center rounded-md bg-${viewType === mode ? 'vamos-green1' : 'vamos-white'}`}
                    />
                  </button>
                </ViewModeTooltip>
              ))}
            </div>
          </div>
          {renderView()}
        </div>
        {isTagModalOpen && (
          <TagModal
            handleClose={() => setIsTagModalOpen(false)}
            siteIds={sites.map((s) => s.id)}
            orgId={organizationId}
            existingTags={existingTags}
            title="Tags"
            isTagAsTabShowing
            isCreateNewTabShowing
            isTagShowPage
            onTagsUpdated={(count) => showToast({ message: `Tags updated for ${count} site${count === 1 ? '' : 's'}.`, type: 'success' })}
          />
        )}
        {viewType !== 'map' && (
          <footer>
            <Pagination
              itemsPerPage={limit}
              totalItems={totalSites}
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
            />
          </footer>
        )}
      </>
    );
  };

  return (
    <DialogsProvider>
      <div className="flex flex-col">
        <div className="ml-2.75">
          <header>
            <div className="flex justify-between items-start w-full">
              {/* Breadcrumbs */}
              <Breadcrumbs links={breadcrumbLinks} />
              {/* Search Section */}
              <div className="self-start">{renderSearchSection()}</div>
            </div>
          </header>
          {renderContent()}
        </div>
      </div>
    </DialogsProvider>
  );
};

SitesPage.propTypes = {
  organizationId: PropTypes.number.isRequired,
};

export default withContextProvider(ToastProvider)(SitesPage);
